import {
  VESSEL_SEARCH_GHG_RATINGS,
  VESSEL_SEARCH_SORT_COLUMNS,
  VESSEL_SEARCH_TC_STATUSES,
} from "../models/advanced-vessel-search.constants";
import { AdvancedVesselSearchFilters, AdvancedVesselSearchPanelProps } from "../models/advanced-vessel-search.models";
import { FormEvent, useState } from "react";
import { twMerge } from "tailwind-merge";

// Owns the unfinished filter draft so typing here never triggers a fleet query.
// Applying submits one combined search; resetting restores the existing cached search.
export function AdvancedVesselSearchPanel({ filters, onApply, onClose }: AdvancedVesselSearchPanelProps) {
  const [draft, setDraft] = useState(filters);
  const controlClass =
    "mt-1 h-9 w-full rounded border border-gray-300 bg-white px-2 text-sm text-gray-800 focus:border-green focus:outline-none";
  const updateField = (field: keyof AdvancedVesselSearchFilters, value: string) => {
    setDraft((previous) => ({ ...previous, [field]: value || undefined }));
  };
  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply(draft);
    onClose();
  };
  const updateSortField = (value: string) => {
    setDraft((previous) => ({
      ...previous,
      sortBy: (value || undefined) as AdvancedVesselSearchFilters["sortBy"],
      sortDirection: value ? previous.sortDirection || "asc" : undefined,
    }));
  };

  const resetFilters = () => {
    onApply({});
    onClose();
  };

  return (
    <form
      id="advanced-vessel-search-panel"
      role="dialog"
      aria-labelledby="advanced-vessel-search-title"
      data-test="advanced-vessel-search-panel"
      onSubmit={applyFilters}
      className="absolute left-0 top-full z-40 mt-2 max-h-[calc(100dvh-9rem)] w-[900px] max-w-[calc(100vw-8rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-5 text-gray-800 shadow-xl"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 id="advanced-vessel-search-title" className="text-base font-semibold">
          Search and filter vessels
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close vessel filters"
          data-test="advanced-vessel-search-close"
          className="rounded px-2 py-1 focus-visible:outline-green"
        >
          Close
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-xs font-medium">
          Fleet activity
          <select
            autoFocus
            className={controlClass}
            value={draft.activity || ""}
            onChange={(event) => updateField("activity", event.target.value)}
            data-test="advanced-vessel-search-activity"
          >
            <option value="">Use inactive-vessels checkbox</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All vessels</option>
          </select>
        </label>
        <label className="text-xs font-medium">
          OpsLink account activation
          <select
            className={controlClass}
            value={draft.opslinkActive || ""}
            onChange={(event) => updateField("opslinkActive", event.target.value)}
            data-test="advanced-vessel-search-opslink-active"
          >
            <option value="">Any</option>
            <option value="true">Activated</option>
            <option value="false">Not activated</option>
          </select>
        </label>
        <label className="text-xs font-medium">
          IMO number
          <input
            className={controlClass}
            value={draft.imo || ""}
            inputMode="numeric"
            pattern="[0-9]{7}"
            maxLength={7}
            onChange={(event) => updateField("imo", event.target.value)}
            data-test="advanced-vessel-search-imo"
          />
        </label>
        <label className="text-xs font-medium">
          Vessel type
          <input
            className={controlClass}
            value={draft.vesselType || ""}
            maxLength={100}
            onChange={(event) => updateField("vesselType", event.target.value)}
            data-test="advanced-vessel-search-type"
          />
        </label>
        <label className="text-xs font-medium">
          Vessel owner
          <input
            className={controlClass}
            value={draft.owner || ""}
            maxLength={200}
            onChange={(event) => updateField("owner", event.target.value)}
            data-test="advanced-vessel-search-owner"
          />
        </label>
        <label className="text-xs font-medium">
          Minimum DWT
          <input
            type="number"
            min="0"
            max={draft.dwtMax || "1000000"}
            step="any"
            className={controlClass}
            value={draft.dwtMin || ""}
            onChange={(event) => updateField("dwtMin", event.target.value)}
            data-test="advanced-vessel-search-dwt-min"
          />
        </label>
        <label className="text-xs font-medium">
          Maximum DWT
          <input
            type="number"
            min={draft.dwtMin || "0"}
            max="1000000"
            step="any"
            className={controlClass}
            value={draft.dwtMax || ""}
            onChange={(event) => updateField("dwtMax", event.target.value)}
            data-test="advanced-vessel-search-dwt-max"
          />
        </label>
        <label className="text-xs font-medium">
          Minimum safety score
          <input
            type="number"
            min="0"
            max={draft.safetyMax || "5"}
            step="0.1"
            className={controlClass}
            value={draft.safetyMin || ""}
            onChange={(event) => updateField("safetyMin", event.target.value)}
            data-test="advanced-vessel-search-safety-min"
          />
        </label>
        <label className="text-xs font-medium">
          Maximum safety score
          <input
            type="number"
            min={draft.safetyMin || "0"}
            max="5"
            step="0.1"
            className={controlClass}
            value={draft.safetyMax || ""}
            onChange={(event) => updateField("safetyMax", event.target.value)}
            data-test="advanced-vessel-search-safety-max"
          />
        </label>
        <label className="text-xs font-medium">
          CP date from
          <input
            type="date"
            max={draft.cpDateTo}
            className={controlClass}
            value={draft.cpDateFrom || ""}
            onChange={(event) => updateField("cpDateFrom", event.target.value)}
            data-test="advanced-vessel-search-cp-from"
          />
        </label>
        <label className="text-xs font-medium">
          CP date to
          <input
            type="date"
            min={draft.cpDateFrom}
            className={controlClass}
            value={draft.cpDateTo || ""}
            onChange={(event) => updateField("cpDateTo", event.target.value)}
            data-test="advanced-vessel-search-cp-to"
          />
        </label>
        <label className="text-xs font-medium">
          Sort by
          <select
            className={controlClass}
            value={draft.sortBy || ""}
            onChange={(event) => updateSortField(event.target.value)}
            data-test="advanced-vessel-search-sort"
          >
            <option value="">Default order</option>
            {VESSEL_SEARCH_SORT_COLUMNS.map((column) => (
              <option key={column.field} value={column.field}>
                {column.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium">
          Sort direction
          <select
            disabled={!draft.sortBy}
            className={controlClass}
            value={draft.sortDirection || "asc"}
            onChange={(event) => updateField("sortDirection", event.target.value)}
            data-test="advanced-vessel-search-direction"
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
        <label className="text-xs font-medium">
          GHG ratings
          <select
            multiple
            className={twMerge(controlClass, "h-28")}
            value={draft.ghgRatings?.split(",") || []}
            onChange={(event) =>
              updateField("ghgRatings", Array.from(event.target.selectedOptions, (option) => option.value).join(","))
            }
            data-test="advanced-vessel-search-ghg"
          >
            {VESSEL_SEARCH_GHG_RATINGS.map((rating) => (
              <option key={rating} value={rating}>
                {rating}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium">
          Time charter statuses
          <select
            multiple
            className={twMerge(controlClass, "h-28")}
            value={draft.tcStatuses?.split(",") || []}
            onChange={(event) =>
              updateField("tcStatuses", Array.from(event.target.selectedOptions, (option) => option.value).join(","))
            }
            data-test="advanced-vessel-search-tc-statuses"
          >
            {VESSEL_SEARCH_TC_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        Leave ratings and statuses unselected for any value. Hold Ctrl (Command on Mac) to select multiple options.
        CP-date range applies to the date shown in the table.
      </p>
      <div className="mt-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={resetFilters}
          data-test="advanced-vessel-search-reset"
          className="rounded border border-gray-300 px-4 py-2 text-sm"
        >
          Reset filters and sort
        </button>
        <button
          type="submit"
          data-test="advanced-vessel-search-apply"
          className="rounded bg-green px-4 py-2 text-sm font-semibold text-white"
        >
          Apply search
        </button>
      </div>
    </form>
  );
}
